import React from 'react'
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  Control,
  Controller,
} from 'react-hook-form'
import { SDGsCategoryValues } from '../../../enums/SDGsCategory'
import {
  FormField,
  TextInput,
  TextareaInput,
  CheckboxInput,
  RadioInput,
} from '../../../components/form'
import { FormErrorMessage } from '../../../constants/message'
import { useImageUpload } from '../../../hooks/use_image_upload'
import { useLoading } from '../../../providers/loading_provider'
import { RecruitmentStatusValue } from '../../../enums/RecruitmentStatus'
import { QuillEditor } from '../../../components/form/inputs/QuillEditor'

export type RecruitmentFormData = {
  title: string
  description: string
  requirements?: string
  legalMatters?: string
  thumbnailUrl: string
  status: RecruitmentStatusValue
  scheduledDate?: string
  sdgsCategories: string[]
}

interface RecruitmentFormLayoutProps {
  title: string
  onSubmit: (e: React.FormEvent) => void
  register: UseFormRegister<RecruitmentFormData>
  errors: FieldErrors<RecruitmentFormData>
  isSubmitting: boolean
  isEditMode?: boolean
  currentStatus?: RecruitmentStatusValue
  onCancel: () => void
  onSaveDraft?: () => void
  watch: UseFormWatch<RecruitmentFormData>
  setValue: UseFormSetValue<RecruitmentFormData>
  control: Control<RecruitmentFormData>
}

const RecruitmentMainContent: React.FC<{
  register: UseFormRegister<RecruitmentFormData>
  errors: FieldErrors<RecruitmentFormData>
  control: Control<RecruitmentFormData>
}> = ({ register, errors, control }) => {
  return (
    <>
      {/* タイトル */}
      <div className="bg-white p-6 rounded">
        <FormField label="タイトル" required>
          <TextInput
            register={register('title', {
              required: FormErrorMessage.required,
            })}
            placeholder="募集タイトルを入力してください"
          />
        </FormField>
        {errors.title && (
          <p className="text-main-red text-sm mt-1">{errors.title.message}</p>
        )}
      </div>

      {/* 本文 */}
      <div className="bg-white p-6 rounded">
        <FormField label="本文" required>
          <Controller
            name="description"
            control={control}
            rules={{
              required: FormErrorMessage.required,
            }}
            render={({ field }) => (
              <QuillEditor
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="募集内容を入力してください"
                className="min-h-[300px]"
              />
            )}
          />
        </FormField>
        {errors.description && (
          <p className="text-main-red text-sm mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* 応募要件 */}
      <div className="bg-white p-6 rounded">
        <FormField label="応募要件">
          <TextareaInput
            register={register('requirements')}
            rows={4}
            placeholder="応募要件を入力してください"
          />
        </FormField>
      </div>

      {/* 法的事項 */}
      <div className="bg-white p-6 rounded">
        <FormField label="法的事項">
          <TextareaInput
            register={register('legalMatters')}
            rows={4}
            placeholder="法的事項を入力してください"
          />
        </FormField>
      </div>

      {/* SDGsカテゴリー */}
      <div className="bg-white p-6 rounded">
        <FormField label="SDGsカテゴリー" required>
          <CheckboxInput
            name="sdgsCategories"
            register={register('sdgsCategories', {
              required: FormErrorMessage.required,
            })}
            options={SDGsCategoryValues.map((category) => ({
              value: category.code,
              label: `${category.code}：${category.value}`,
            }))}
          />
        </FormField>
        {errors.sdgsCategories && (
          <p className="text-main-red text-sm mt-1">
            {errors.sdgsCategories.message}
          </p>
        )}
      </div>
    </>
  )
}

const RecruitmentSidebar: React.FC<{
  register: UseFormRegister<RecruitmentFormData>
  isSubmitting: boolean
  isEditMode: boolean
  currentStatus?: 'draft' | 'published' | 'scheduled'
  onCancel: () => void
  onSaveDraft?: () => void
  watch: UseFormWatch<RecruitmentFormData>
  setValue: UseFormSetValue<RecruitmentFormData>
}> = ({
  register,
  isSubmitting,
  isEditMode,
  currentStatus,
  onCancel,
  onSaveDraft,
  watch,
  setValue,
}) => {
  const { upload } = useImageUpload()
  const { showLoading, hideLoading } = useLoading()

  // サムネイルアップロード処理
  const uploadThumbnail = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (!files || files?.length === 0) return

    showLoading()
    const nextFile = files[0]
    const response = await upload(nextFile)
    if (response !== null) {
      setValue('thumbnailUrl', response.url)
    }
    hideLoading()
  }
  return (
    <div className="flex flex-row lg:flex-col gap-6 items-start">
      {/* 左側の要素グループ（lg以下では左、lg以上では上） */}
      <div className="space-y-6">
        {/* ヘッダーボタン */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 text-xs text-main-darkgray border border-main-lightgray rounded bg-main-white hover:bg-main-lightgray transition-colors"
          >
            キャンセル
          </button>
          {onSaveDraft && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={isSubmitting}
              className="px-3 py-1 text-xs border border-main-green text-main-green rounded bg-main-white hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下書き保存
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1 text-xs bg-main-green text-main-white rounded hover:bg-main-lightgreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? isEditMode
                ? '更新中...'
                : '公開中...'
              : isEditMode
                ? '更新する'
                : '公開する'}
          </button>
        </div>

        {/* ステータス */}
        <div>
          <h2 className="text-sm font-bold mb-2 text-main-black">ステータス</h2>
          <p className="text-xs text-main-darkgray">
            {currentStatus === 'published'
              ? '公開中'
              : currentStatus === 'scheduled'
                ? '予約投稿'
                : '下書き'}
          </p>
        </div>

        {/* 公開設定 */}
        <div>
          <FormField label="公開設定">
            <RadioInput
              name="status"
              register={register('status')}
              options={[
                { value: 'published', label: 'すぐに公開する' },
                { value: 'scheduled', label: '予約投稿する' },
              ]}
            />
            {watch('status') === 'scheduled' && (
              <div className="mt-3">
                <input
                  type="date"
                  {...register('scheduledDate')}
                  className="w-full px-2 py-1 text-xs border border-main-lightgray rounded focus:outline-none focus:ring-1 focus:ring-main-green"
                  min={(() => {
                    const today = new Date()
                    const year = today.getFullYear()
                    const month = String(today.getMonth() + 1).padStart(2, '0')
                    const day = String(today.getDate()).padStart(2, '0')
                    return `${year}-${month}-${day}`
                  })()}
                />
              </div>
            )}
          </FormField>
        </div>

        {/* プレビュー */}
        <div>
          <h2 className="text-sm font-bold mb-2 text-main-black">プレビュー</h2>
          <button
            type="button"
            className="text-xs text-main-green hover:text-main-darkgray underline"
          >
            別タブでプレビュー表示
          </button>
        </div>
      </div>

      {/* サムネイル（lg以下では右、lg以上では下） */}
      <div className="flex-shrink-0 w-48 lg:w-full">
        <h2 className="text-sm font-bold mb-2 text-main-black">サムネイル</h2>
        <div className="aspect-square bg-main-gray rounded flex items-center justify-center mb-2">
          {watch('thumbnailUrl') ? (
            <div
              className="w-full h-full bg-cover rounded bg-center"
              style={{
                backgroundImage: `url(${watch('thumbnailUrl')})`,
              }}
            ></div>
          ) : (
            <div className="bg-main-white p-2 rounded">
              <span className="material-icons !text-2xl text-main-darkgray">
                photo
              </span>
            </div>
          )}
        </div>
        <label
          htmlFor="thumbnail-upload"
          className="inline-block px-3 py-1 text-xs bg-main-green text-main-white rounded hover:bg-main-lightgreen transition-colors cursor-pointer"
        >
          画像アップロード
        </label>
        <input
          id="thumbnail-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={uploadThumbnail}
        />
        <div className="text-xs text-main-darkgray mt-1">
          ※データ形式はjpg、pngのみ対応
        </div>
      </div>
    </div>
  )
}

export const RecruitmentFormLayout: React.FC<RecruitmentFormLayoutProps> = ({
  title,
  onSubmit,
  register,
  errors,
  isSubmitting,
  isEditMode = false,
  currentStatus = 'draft',
  onCancel,
  onSaveDraft,
  watch,
  setValue,
  control,
}) => {
  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* ページヘッダー */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-800">{title}</h1>
        </div>

        <form
          onSubmit={onSubmit}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* 左側：メインコンテンツ */}
          <div className="lg:col-span-2 space-y-4">
            <RecruitmentMainContent
              register={register}
              errors={errors}
              control={control}
            />
          </div>

          {/* 右側：サイドバー */}
          <div className="lg:col-span-1">
            <div className="bg-main-white p-4 rounded border border-main-lightgray space-y-6">
              <RecruitmentSidebar
                register={register}
                isSubmitting={isSubmitting}
                isEditMode={isEditMode}
                currentStatus={currentStatus}
                onCancel={onCancel}
                onSaveDraft={onSaveDraft}
                watch={watch}
                setValue={setValue}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
